import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { ArrowRight, Layers, Building2, Leaf, Search, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import ListingCard from '../components/ListingCard';
import MapHeatmap from '../components/MapHeatmap';
import { devLog, devError, devWarn } from '../utils/logger';

const heroImage = 'https://images.unsplash.com/photo-1612776697491-6f2e4c62d69b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxhbnRpcXVlJTIwYnJhc3MlMjBkb29yJTIwaGFuZGxlcyUyMHJlY2xhaW1lZCUyMHdvb2QlMjBiZWFtcyUyMHZpbnRhZ2UlMjBicmljayUyMHdhbGwlMjBhcmNoaXRlY3QlMjBleGFtaW5pbmclMjBibHVlcHJpbnRzJTIwbHV4dXJ5JTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBkYXJrfGVufDB8fHx8MTc3Mzk5MTQxM3ww&ixlib=rb-4.1.0&q=85';

const features = [
  {
    icon: Layers,
    title: 'Digital BIM Models',
    description: 'Access 3D models ready for Revit integration. Design sustainably from the start.'
  },
  {
    icon: Building2,
    title: 'Verified Suppliers',
    description: 'Connect with trusted salvage operators and demolition companies worldwide.'
  },
  {
    icon: Leaf,
    title: 'Sustainable Building',
    description: 'Reduce construction waste and carbon footprint with reclaimed materials.'
  }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [featuredListings, setFeaturedListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [listingsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/listings/featured?limit=8`),
          axios.get(`${API}/listings/categories`)
        ]);
        setFeaturedListings(listingsRes.data);
        setCategories(categoriesRes.data.categories);
      } catch (error) {
        devError('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/marketplace?search=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient pt-16">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={heroImage} 
            alt="Reclaimed materials" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-accent mb-6 font-manrope animate-fadeIn">
              The Circular Economy Marketplace
            </p>
            <h1 className="font-playfair text-5xl md:text-7xl font-bold text-foreground leading-none mb-6 animate-slideUp">
              Where History
              <br />
              <span className="text-gold-gradient">Meets Design</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-10 max-w-xl animate-slideUp stagger-2">
              Discover exceptional salvaged building materials. From Victorian doors to industrial beams — 
              curated for architects, designers, and visionaries.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-10 animate-slideUp stagger-3">
              <div className="flex gap-2 max-w-lg">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search materials, categories..."
                    className="w-full h-14 pl-12 pr-4 bg-secondary/50 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50 focus:border-accent/60"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button type="submit" className="h-14 px-8 btn-gold" data-testid="hero-search-button">
                  Search
                </Button>
              </div>
            </form>

            <div className="flex flex-wrap gap-4 animate-slideUp stagger-4">
              <Button 
                onClick={() => navigate('/marketplace')}
                className="btn-gold px-8 py-6 text-base"
                data-testid="browse-marketplace-btn"
              >
                Browse Marketplace
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/auth?mode=register&type=supplier')}
                className="border-accent/40 text-accent hover:bg-accent/10 px-8 py-6 text-base"
                data-testid="become-supplier-btn"
              >
                Become a Supplier
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section — compact horizontal cards (icon + text side-by-side
          instead of stacked) so the row takes ~half the vertical space. */}
      <section className="py-14 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className={`flex items-start gap-4 p-5 rounded-lg bg-card/50 border border-border/20 hover:border-accent/30 transition-all duration-300 animate-slideUp stagger-${index + 1}`}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-playfair text-base md:text-lg font-semibold text-foreground mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Categories</p>
              <h2 className="font-playfair text-4xl font-bold text-foreground">Browse by Type</h2>
            </div>
            <Link 
              to="/marketplace" 
              className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {categories.slice(0, 6).map((category) => (
              <Link
                key={category.id}
                to={`/marketplace?category=${category.id}`}
                className="p-6 rounded-lg bg-card/50 border border-border/20 hover:border-accent/30 hover:bg-card/80 transition-all duration-300 text-center group"
                data-testid={`category-${category.id}`}
              >
                <p className="font-manrope text-sm font-medium text-foreground group-hover:text-accent transition-colors">
                  {category.name}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Live supply heatmap */}
      <section className="py-24 border-t border-border/10">
        <MapHeatmap />
      </section>

      {/* Featured Listings Section */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Hand-picked</p>
              <h2 className="font-playfair text-4xl font-bold text-foreground">Featured Materials</h2>
            </div>
            <Link 
              to="/marketplace" 
              className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={`skeleton-${i}`} className="aspect-[4/3] rounded-lg bg-card/50 animate-pulse" />
              ))}
            </div>
          ) : featuredListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-listings-grid">
              {featuredListings.slice(0, 8).map((listing) => (
                <ListingCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-muted-foreground mb-4">No listings yet. Be the first to add materials!</p>
              <Button onClick={() => navigate('/auth?mode=register&type=supplier')} className="btn-gold">
                Become a Supplier
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-primary/20 to-accent/10 p-12 md:p-16">
            <div className="relative z-10 max-w-2xl">
              <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-6">
                Ready to Build
                <br />
                <span className="text-gold-gradient">Sustainably?</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Join thousands of architects and designers who are transforming the construction industry 
                through circular economy practices.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => navigate('/auth?mode=register')}
                  className="btn-gold px-8 py-6"
                  data-testid="cta-get-started-btn"
                >
                  Get Started Free
                </Button>
                <a href="/revit" data-testid="cta-revit-download-link">
                  <Button 
                    variant="outline"
                    className="border-border/40 px-8 py-6"
                  >
                    Download Revit Plugin
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
